import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import {
  EstatusAsistencia,
  MonitoreoFiltro,
  MonitoreoRenglon,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { soloFecha, soloHora } from "../formato-fecha";

/**
 * Monitoreo de asistencia.
 *
 * Es la pantalla del dia para quien supervisa y, con otro rango y el
 * filtro de cliente, el reporte mensual que se le entrega a ese cliente.
 * Son la MISMA consulta a proposito: dos pantallas parecidas acaban
 * diciendo cosas distintas del mismo dia.
 *
 * El resumen de arriba no se calcula aparte: sale del mismo filtro sin
 * paginar, para que el total y el desglose no puedan discrepar.
 */
@Component({
  selector: "app-monitoreo",
  standalone: true,
  templateUrl: "./monitoreo.component.html",
  styleUrls: ["./monitoreo.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class MonitoreoComponent implements OnInit {
  renglones: MonitoreoRenglon[] = [];
  ubicaciones: Ubicacion[] = [];
  clientes: { id: number; nombre: string }[] = [];
  resumen: { [estatus: string]: number } = {};
  loading = false;

  filtroDesde: Date = new Date();
  filtroHasta: Date = new Date();
  filtroUbicacion: number = null;
  filtroCliente: number = null;
  filtroEstatus: string = null;
  filtroModalidad: string = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];
  totalRegistros = 0;

  /** Orden de lectura: primero lo que exige atencion. */
  readonly estatusVisibles: EstatusAsistencia[] = [
    "FALTA",
    "RETARDO",
    "PENDIENTE",
    "PUNTUAL",
    "JUSTIFICADO",
    "VACACIONES",
    "PERMISO",
    "DESCANSO",
    "SIN_HORARIO",
  ];

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarUbicaciones();
    queueMicrotask(() => {
      this.consultaPagina(1);
      this.cdr.detectChanges();
    });
  }

  consultaPagina(pagina: number): void {
    this.currentPage = pagina;
    const filtro = this.armarFiltro();
    filtro.rowInicio = (pagina - 1) * this.pageSize;
    filtro.regPagina = this.pageSize;

    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarMonitoreo(filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.renglones = respuesta?.list ?? [];
          this.totalRegistros = respuesta?.pTotalItems ?? 0;
          if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
            this.msg.warningCall(respuesta.pMensaje, null, null);
          }
          this.cargarResumen();
        },
        error: () => {
          this.renglones = [];
          this.totalRegistros = 0;
          this.msg.errorCall("No se pudo consultar la asistencia.", null, null);
        },
      });
  }

  limpiarFiltros(): void {
    this.filtroDesde = new Date();
    this.filtroHasta = new Date();
    this.filtroUbicacion = null;
    this.filtroCliente = null;
    this.filtroEstatus = null;
    this.filtroModalidad = null;
    this.consultaPagina(1);
  }

  /**
   * Exporta lo que se esta viendo a CSV, sin paginar.
   *
   * Se genera en el navegador y no en el servidor porque el dato ya
   * viajo: pedirlo otra vez solo para darle formato duplicaria la
   * consulta y abriria la puerta a que las dos difieran.
   */
  exportarCsv(): void {
    const filtro = this.armarFiltro();
    filtro.rowInicio = null;
    filtro.regPagina = null;

    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarMonitoreo(filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => this.descargarCsv(respuesta?.list ?? []),
        error: () => this.msg.errorCall("No se pudo generar el archivo.", null, null),
      });
  }

  soloHora(valor: string | Date): string {
    return soloHora(valor);
  }

  soloFecha(valor: string | Date): string {
    return soloFecha(valor);
  }

  etiquetaEstatus(estatus: EstatusAsistencia): string {
    const etiquetas: Record<string, string> = {
      PUNTUAL: "Puntual",
      RETARDO: "Retardo",
      FALTA: "Falta",
      SIN_HORARIO: "Sin horario",
      DESCANSO: "Descanso",
      VACACIONES: "Vacaciones",
      PERMISO: "Permiso",
      JUSTIFICADO: "Justificado",
      PENDIENTE: "Pendiente",
    };
    return etiquetas[estatus] ?? estatus;
  }

  /**
   * Con que se registro la entrada: separa a quien llego con su huella de
   * quien registro desde su telefono. El reporte lo lleva en su propia
   * columna.
   *
   * Solo existe para la ENTRADA: la salida cierra la misma fila y no anota
   * por donde llego (RegistroChecadaService.cerrarJornada).
   */
  etiquetaOrigen(origen: string): string {
    const etiquetas: Record<string, string> = {
      HUELLA: "Huella",
      APP: "Teléfono",
      QR: "Código QR",
      WEB: "Web",
      MANUAL: "Captura manual",
    };
    return origen ? etiquetas[origen] ?? origen : "";
  }

  /** En una lista larga la forma se distingue antes que la palabra. */
  iconoOrigen(origen: string): string {
    const iconos: Record<string, string> = {
      HUELLA: "fingerprint",
      APP: "smartphone",
      QR: "qr_code_2",
      WEB: "language",
      MANUAL: "edit_note",
    };
    return iconos[origen] ?? "login";
  }

  /**
   * La forma tambien dice el estado, no solo el color: quien no
   * distingue rojo de verde tiene que poder leer la tabla igual.
   */
  claseEstatus(estatus: EstatusAsistencia): string {
    // Modificadores del estandar table-page (styles.css + temas):
    // is-danger / is-pending / is-active / is-inactive. Los antiguos
    // --danger/--warning/--success no existen en ningun CSS y dejaban el
    // chip sin color. El resto (pendiente, vacaciones, permiso, descanso,
    // sin horario) va como is-inactive, igual que en mi-asistencia: la
    // clase base sola no pinta fondo y el chip quedaba como texto suelto.
    if (estatus === "FALTA") {
      return "table-page__status-badge is-danger";
    }
    if (estatus === "RETARDO") {
      return "table-page__status-badge is-pending";
    }
    if (estatus === "PUNTUAL" || estatus === "JUSTIFICADO") {
      return "table-page__status-badge is-active";
    }
    return "table-page__status-badge is-inactive";
  }

  etiquetaModalidad(renglon: MonitoreoRenglon): string {
    const am = this.nombreModalidad(renglon.modalidadAm);
    const pm = this.nombreModalidad(renglon.modalidadPm);
    if (!am && !pm) {
      return "Sin asignar";
    }
    if (!pm || am === pm) {
      return am;
    }
    return `${am} / ${pm}`;
  }

  /** Donde se le esperaba; para home office no hay sitio y eso es correcto. */
  etiquetaSitio(renglon: MonitoreoRenglon): string {
    if (!renglon.nombreUbicacion) {
      return "Sin sitio";
    }
    return renglon.nombreCliente
      ? `${renglon.nombreUbicacion} · ${renglon.nombreCliente}`
      : renglon.nombreUbicacion;
  }

  totalDe(estatus: EstatusAsistencia): number {
    return this.resumen[estatus] ?? 0;
  }

  get hayResumen(): boolean {
    return Object.keys(this.resumen).length > 0;
  }

  // ------------------------------------------------------------------
  // Paginacion
  // ------------------------------------------------------------------

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRegistros / this.pageSize));
  }

  get showPagination(): boolean {
    return this.totalRegistros > this.pageSize;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  onPageChange(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPages && pagina !== this.currentPage) {
      this.consultaPagina(pagina);
    }
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onPageSizeChange(tamano: number): void {
    this.pageSize = tamano;
    this.consultaPagina(1);
  }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const items: Array<number | string> = [1];
    const inicio = Math.max(2, this.currentPage - 1);
    const fin = Math.min(total - 1, this.currentPage + 1);
    if (inicio > 2) {
      items.push("...");
    }
    for (let p = inicio; p <= fin; p++) {
      items.push(p);
    }
    if (fin < total - 1) {
      items.push("...");
    }
    items.push(total);
    return items;
  }

  isPageNumber(item: number | string): boolean {
    return typeof item === "number";
  }

  // ------------------------------------------------------------------

  private armarFiltro(): MonitoreoFiltro {
    const filtro = new MonitoreoFiltro();
    filtro.desde = this.aIsoFecha(this.filtroDesde);
    filtro.hasta = this.aIsoFecha(this.filtroHasta);
    filtro.idUbicacion = this.filtroUbicacion;
    filtro.idCliente = this.filtroCliente;
    filtro.estatus = this.filtroEstatus;
    filtro.modalidad = this.filtroModalidad;
    return filtro;
  }

  private cargarResumen(): void {
    const filtro = this.armarFiltro();
    this.asistenciaService.resumenMonitoreo(filtro).subscribe({
      next: (resumen) => {
        this.resumen = resumen ?? {};
        this.cdr.detectChanges();
      },
      error: () => {
        this.resumen = {};
      },
    });
  }

  private cargarUbicaciones(): void {
    this.asistenciaService.consultarUbicaciones(null).subscribe({
      next: (respuesta) => {
        this.ubicaciones = respuesta?.list ?? [];
        this.clientes = this.clientesDeLosSitios();
        this.cdr.detectChanges();
      },
      error: () => {
        this.ubicaciones = [];
        this.clientes = [];
      },
    });
  }

  /**
   * Los clientes salen de los sitios ya cargados, no de otra consulta.
   *
   * Un cliente sin sitio no tiene a nadie a quien monitorear, asi que
   * ofrecerlo en el filtro solo daria una lista vacia; y evitar el viaje de
   * mas mantiene la pantalla con una sola dependencia.
   */
  private clientesDeLosSitios(): { id: number; nombre: string }[] {
    const porId = new Map<number, string>();
    for (const sitio of this.ubicaciones) {
      if (sitio.idCliente != null && sitio.nombreCliente) {
        porId.set(sitio.idCliente, sitio.nombreCliente);
      }
    }
    return Array.from(porId, ([id, nombre]) => ({ id, nombre })).sort((a, b) =>
      a.nombre.localeCompare(b.nombre)
    );
  }

  /**
   * Al elegir cliente se suelta el sitio: dejar los dos puestos produce una
   * lista vacia en cuanto el sitio es de otro cliente, y eso se lee como
   * "este cliente no tiene gente" en vez de como un filtro contradictorio.
   */
  cambioCliente(): void {
    this.filtroUbicacion = null;
    this.consultaPagina(1);
  }

  /** Los sitios que se pueden elegir con el cliente actualmente filtrado. */
  get ubicacionesVisibles(): Ubicacion[] {
    if (this.filtroCliente == null) {
      return this.ubicaciones;
    }
    return this.ubicaciones.filter(
      (sitio) => sitio.idCliente === this.filtroCliente
    );
  }

  /**
   * yyyy-MM-dd tomado de las partes LOCALES de la fecha.
   *
   * toISOString() convierte a UTC y en Mexico eso resta horas: una fecha
   * elegida como "1 de septiembre" viajaria como 31 de agosto.
   */
  private aIsoFecha(fecha: Date): string {
    if (!fecha) {
      return null;
    }
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  private descargarCsv(renglones: MonitoreoRenglon[]): void {
    if (!renglones.length) {
      this.msg.warningCall("No hay registros para exportar.", null, null);
      return;
    }

    const encabezado = [
      "Fecha",
      "Empleado",
      "Usuario",
      "Sitio",
      "Cliente",
      "Modalidad",
      "Entrada esperada",
      "Entrada real",
      "Origen de la entrada",
      "Salida real",
      "Minutos de retardo",
      "Minutos trabajados",
      "Estatus",
    ];

    // Es el reporte que se le entrega al cliente: las horas van como en la
    // tabla (HH:mm) y no como las manda el servidor.
    const filas = renglones.map((r) => [
      r.fecha,
      r.nombreEmpleado,
      r.usuario,
      r.nombreUbicacion,
      r.nombreCliente,
      this.etiquetaModalidad(r),
      soloHora(r.horaEsperadaEntrada),
      this.horaDelReporte(r.horaEntradaReal, r.fecha),
      this.etiquetaOrigen(r.origenEntrada),
      this.horaDelReporte(r.horaSalidaReal, r.fecha),
      r.minutosRetardo,
      r.minutosTrabajados,
      this.etiquetaEstatus(r.estatus),
    ]);

    const csv = [encabezado, ...filas]
      .map((fila) => fila.map((celda) => this.aCeldaCsv(celda)).join(","))
      .join("\r\n");

    // BOM para que Excel en Windows lea los acentos.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const enlace = document.createElement("a");
    enlace.href = URL.createObjectURL(blob);
    enlace.download = `asistencia-${this.aIsoFecha(this.filtroDesde)}-a-${this.aIsoFecha(
      this.filtroHasta
    )}.csv`;
    enlace.click();
    URL.revokeObjectURL(enlace.href);
  }

  /**
   * HH:mm de un registro, con el dia delante solo si cae en otra fecha que
   * la del renglon: la hora sola diria que salio ese mismo dia. Igual que
   * horaDelReporte del kiosco (exportar_monitoreo.dart).
   */
  private horaDelReporte(valor: string, fecha: string): string {
    const hora = soloHora(valor);
    if (!hora || !`${valor}`.includes("T")) {
      return hora;
    }
    const dia = soloFecha(valor);
    return dia === soloFecha(fecha) ? hora : `${dia} ${hora}`;
  }

  private aCeldaCsv(valor: unknown): string {
    if (valor === null || valor === undefined) {
      return "";
    }
    const texto = `${valor}`.replaceAll('"', '""');
    return `"${texto}"`;
  }

  private nombreModalidad(modalidad: string): string {
    const nombres: Record<string, string> = {
      OFICINA: "Oficina",
      HOME_OFFICE: "Home office",
      MIXTO: "Mixto",
    };
    return modalidad ? nombres[modalidad] ?? modalidad : "";
  }
}
